import GenericAPIService from './generic-api-service';
import { instance } from './instance';

import type {
  ActivateUserRequest,
  CreateUserRequest,
  UpdateUserRequest,
  ListContentsRequest,
  LoginRequest,
  OrganizationUsersRequest,
  SendActivationEmailRequest,
  SendResetPasswordMailRequest,
  UpdateContentStatusRequest,
  UpdateContentRankingRequest,
  CreateContentRequest,
  UpdateContentRequest,
  ConsumerRegistrationDeletionRequest,
  ReportsRequest,
  ConsumerSearchRequest,
  NotificationsSearchRequest,
  CreateNotificationRequest,
  UpdateNotificationRequest,
  CreateBranchRequest,
  UpdateBranchRequest,
  DeleteBranchRequest,
  ListBranchesRequest,
  ImportExcelRequest,
  OrganizationMenuCategoriesSearchRequest,
  CreateOrganizationMenuCategoryRequest,
  UpdateOrganizationMenuCategoryRequest,
  CreateOrganizationMenuRequest,
  OrganizationMenusListResponse,
  UpdateOrganizationMenuRequest,
  OrganizationMenuProductsSearchRequest,
  OrganizationMenuProductDetailResponse,
  CreateOrganizationMenuProductRequest,
  UpdateOrganizationMenuProductRequest,
  StartCampaignReportRequest,
} from '../schemas/auth';

import {
  activateUserResponseSchema,
  createUserResponseSchema,
  updateUserResponseSchema,
  listContentsResponseSchema,
  listSystemRolesResponseSchema,
  organizationUsersResponseSchema,
  organizationRolesDropdownResponseSchema,
  organizationUserDetailResponseSchema,
  sendActivationEmailResponseSchema,
  sendResetPasswordMailResponseSchema,
  deleteUserResponseSchema,
  updateContentStatusResponseSchema,
  updateContentRankingResponseSchema,
  createContentResponseSchema,
  updateContentResponseSchema,
  contentDetailResponseSchema,
  deleteContentResponseSchema,
  reportAuthenticationsResponseSchema,
  consumerRegistrationDeletionResponseSchema,
  reportsResponseSchema,
  consumerSearchResponseSchema,
  consumerDetailResponseSchema,
  deleteConsumerResponseSchema,
  notificationsSearchResponseSchema,
  createNotificationResponseSchema,
  updateNotificationResponseSchema,
  notificationDetailResponseSchema,
  deleteNotificationResponseSchema,
  createBranchResponseSchema,
  updateBranchResponseSchema,
  deleteBranchResponseSchema,
  listBranchesResponseSchema,
  branchDetailResponseSchema,
  getBranchTemplateFileResponseSchema,
  importExcelResponseSchema,
  citiesDropdownResponseSchema,
  countiesDropdownResponseSchema,
  organizationMenuCategoriesSearchResponseSchema,
  organizationMenuCategoriesDropdownResponseSchema,
  createOrganizationMenuCategoryResponseSchema,
  organizationMenuCategoryDetailResponseSchema,
  updateOrganizationMenuCategoryResponseSchema,
  deleteOrganizationMenuCategoryResponseSchema,
  createOrganizationMenuResponseSchema,
  organizationMenusListResponseSchema,
  updateOrganizationMenuResponseSchema,
  organizationMenuProductsSearchResponseSchema,
  organizationMenuProductDetailResponseSchema,
  createOrganizationMenuProductResponseSchema,
  updateOrganizationMenuProductResponseSchema,
  deleteOrganizationMenuProductResponseSchema,
  campaignReportStatusSchema,
} from '../schemas/auth';

import { loginResponseSchema } from '../schemas/auth';

// StaticTexts
import {
  languageListSchema,
  staticTextSearchResponseSchema,
  textTypesDropdownResponseSchema,
  createStaticTextResponseSchema,
  updateStaticTextResponseSchema,
  staticTextDetailResponseSchema,
  deleteStaticTextResponseSchema,
  organizationLanguagesResponseSchema,
} from '../schemas/staticTexts';
import type { StaticTextSearchRequest, CreateStaticTextRequest, UpdateStaticTextRequest } from '../schemas/staticTexts';

const request = new GenericAPIService(instance);

export function login(payload: LoginRequest) {
  return request.post<typeof loginResponseSchema, LoginRequest, true>({
    url: '/OrganizationUsers/authenticate',
    parser: loginResponseSchema,
    payload,
    returnData: true,
  });
}

export function createUser(payload: CreateUserRequest) {
  return request.post<typeof createUserResponseSchema, CreateUserRequest, true>({
    url: '/OrganizationUsers',
    parser: createUserResponseSchema,
    payload,
    returnData: true,
  });
}

export function updateUser(id: string, payload: UpdateUserRequest) {
  return request.post<typeof updateUserResponseSchema, UpdateUserRequest, true>({
    url: `/OrganizationUsers/${id}`,
    parser: updateUserResponseSchema,
    payload,
    returnData: true,
  });
}
export function listSystemRoles() {
  return request.get<typeof listSystemRolesResponseSchema, undefined, true>({
    url: '/SystemRoles',
    parser: listSystemRolesResponseSchema,
    returnData: true,
  });
}

export function getOrganizationRolesForDropdown() {
  return request.get<typeof organizationRolesDropdownResponseSchema, undefined, true>({
    url: '/OrganizationRoles/listfordropdown',
    parser: organizationRolesDropdownResponseSchema,
    returnData: true,
  });
}
export function activateUser(payload: ActivateUserRequest) {
  return request.post<typeof activateUserResponseSchema, ActivateUserRequest, true>({
    url: '/OrganizationUsers/activateuser',
    parser: activateUserResponseSchema,
    payload,
    returnData: true,
  });
}

export function getOrganizationUsers(params?: OrganizationUsersRequest) {
  return request.get<typeof organizationUsersResponseSchema, OrganizationUsersRequest, true>({
    url: '/OrganizationUsers',
    parser: organizationUsersResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function getOrganizationUserDetail(id: string) {
  return request.get<typeof organizationUserDetailResponseSchema, undefined, true>({
    url: `/OrganizationUsers/profile`,
    parser: organizationUserDetailResponseSchema,
    returnData: true,
  });
}

export function sendActivationEmail(payload: SendActivationEmailRequest) {
  return request.post<typeof sendActivationEmailResponseSchema, SendActivationEmailRequest, true>({
    url: '/OrganizationUsers/sendactivationmail',
    parser: sendActivationEmailResponseSchema,
    payload,
    returnData: true,
  });
}

export function sendResetPasswordMail(payload: SendResetPasswordMailRequest) {
  return request.post<typeof sendResetPasswordMailResponseSchema, SendResetPasswordMailRequest, true>({
    url: '/OrganizationUsers/sendresetpasswordmail',
    parser: sendResetPasswordMailResponseSchema,
    payload,
    returnData: true,
  });
}

export function deleteUser(id: string) {
  return request.delete<typeof deleteUserResponseSchema, undefined, true>({
    url: `/OrganizationUsers/${id}`,
    parser: deleteUserResponseSchema,
    returnData: true,
  });
}

export function listContents(payload?: ListContentsRequest) {
  return request.get<typeof listContentsResponseSchema, ListContentsRequest, true>({
    url: '/OrganizationContents/search',
    parser: listContentsResponseSchema,
    returnData: true,
    payload,
  });
}

export function updateContentStatus(id: string, payload: UpdateContentStatusRequest) {
  return request.post<typeof updateContentStatusResponseSchema, UpdateContentStatusRequest, true>({
    url: `/OrganizationContents/${id}/status`,
    parser: updateContentStatusResponseSchema,
    payload,
    returnData: true,
  });
}

export function updateContentRanking(payload: UpdateContentRankingRequest) {
  return request.post<typeof updateContentRankingResponseSchema, UpdateContentRankingRequest, true>({
    url: '/OrganizationContents/ranking',
    parser: updateContentRankingResponseSchema,
    payload,
    returnData: true,
  });
}

export function getContentDetail(id: string) {
  return request.get<typeof contentDetailResponseSchema, undefined, true>({
    url: `/OrganizationContents/${id}`,
    parser: contentDetailResponseSchema,
    returnData: true,
  });
}

export function createHeroBanner(payload: CreateContentRequest) {
  const formData = new FormData();

  if (payload.name) {
    formData.append('Name', payload.name);
  }

  formData.append('Type', payload.type);

  if (payload.status) {
    formData.append('Status', payload.status);
  }

  if (payload.redirectUrl) {
    formData.append('RedirectUrl', payload.redirectUrl);
  }

  if (payload.startDate) {
    formData.append('StartDate', payload.startDate);
  }

  if (payload.endDate) {
    formData.append('EndDate', payload.endDate);
  }

  if (payload.title) {
    Object.entries(payload.title).forEach(([lang, title]) => {
      formData.append(`Title[${lang}]`, title);
    });
  }

  if (payload.description) {
    Object.entries(payload.description).forEach(([lang, desc]) => {
      formData.append(`Description[${lang}]`, desc);
    });
  }

  if (payload.shortDescription) {
    Object.entries(payload.shortDescription).forEach(([lang, shortDesc]) => {
      formData.append(`ShortDescription[${lang}]`, shortDesc);
    });
  }

  if (payload.image) {
    payload.image.forEach((img, index) => {
      formData.append(`Image[${index}].LanguageId`, img.languageId);
      formData.append(`Image[${index}].Document`, img.document);
      formData.append(`Image[${index}].EnvironmentType`, img.environmentType);
    });
  }

  return request.postFormData<typeof createContentResponseSchema, true>({
    url: '/OrganizationContents',
    parser: createContentResponseSchema,
    formData,
    returnData: true,
  });
}

export function updateContent(id: string, payload: UpdateContentRequest) {
  const formData = new FormData();

  if (payload.name) {
    formData.append('Name', payload.name);
  }

  formData.append('Type', payload.type);

  if (payload.status) {
    formData.append('Status', payload.status);
  }

  if (payload.rank !== null && payload.rank !== undefined) {
    formData.append('Rank', payload.rank.toString());
  }

  if (payload.redirectUrl) {
    formData.append('RedirectUrl', payload.redirectUrl);
  }

  if (payload.startDate) {
    formData.append('StartDate', payload.startDate);
  }

  if (payload.endDate) {
    formData.append('EndDate', payload.endDate);
  }

  if (payload.title) {
    Object.entries(payload.title).forEach(([lang, title]) => {
      formData.append(`Title[${lang}]`, title);
    });
  }

  if (payload.description) {
    Object.entries(payload.description).forEach(([lang, desc]) => {
      formData.append(`Description[${lang}]`, desc);
    });
  }

  if (payload.shortDescription) {
    Object.entries(payload.shortDescription).forEach(([lang, shortDesc]) => {
      formData.append(`ShortDescription[${lang}]`, shortDesc);
    });
  }

  if (payload.image) {
    payload.image.forEach((img, index) => {
      formData.append(`Image[${index}].LanguageId`, img.languageId);
      formData.append(`Image[${index}].Document`, img.document);
      formData.append(`Image[${index}].EnvironmentType`, img.environmentType);

      if (img.id) {
        formData.append(`Image[${index}].Id`, String(img.id));
      }
    });
  }

  return request.postFormData<typeof updateContentResponseSchema, true>({
    url: `/OrganizationContents/${id}`,
    parser: updateContentResponseSchema,
    formData,
    returnData: true,
  });
}

export function deleteContent(id: string) {
  return request.delete<typeof deleteContentResponseSchema, undefined, true>({
    url: `/OrganizationContents/${id}`,
    parser: deleteContentResponseSchema,
    returnData: true,
  });
}

export function getReportAuthentications() {
  return request.get<typeof reportAuthenticationsResponseSchema, undefined, true>({
    url: '/ReportAuthentications',
    parser: reportAuthenticationsResponseSchema,
    returnData: true,
  });
}

export function getLanguageList() {
  return request.get<typeof languageListSchema, undefined, true>({
    url: '/LanguageList',
    parser: languageListSchema,
    returnData: true,
  });
}

export function listStaticTexts(payload?: StaticTextSearchRequest) {
  return request.get<typeof staticTextSearchResponseSchema, StaticTextSearchRequest, true>({
    url: '/StaticTexts/search',
    parser: staticTextSearchResponseSchema,
    returnData: true,
    payload,
  });
}
export function getConsumerRegistrationDeletion(params?: ConsumerRegistrationDeletionRequest) {
  return request.get<typeof consumerRegistrationDeletionResponseSchema, ConsumerRegistrationDeletionRequest, true>({
    url: '/Reports/consumerregistrationdeletion',
    parser: consumerRegistrationDeletionResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function getReports(params?: ReportsRequest) {
  return request.get<typeof reportsResponseSchema, ReportsRequest, true>({
    url: '/Reports',
    parser: reportsResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function getCampaignReportStatus() {
  return request.get<typeof campaignReportStatusSchema, undefined, true>({
    url: '/campaignreports/status',
    parser: campaignReportStatusSchema,
    returnData: true,
  });
}

export function startCampaignReport(payload: StartCampaignReportRequest) {
  return request.post<typeof campaignReportStatusSchema, StartCampaignReportRequest, true>({
    url: '/campaignreports/start',
    parser: campaignReportStatusSchema,
    payload,
    returnData: true,
  });
}

export function getTextTypesForDropdown() {
  return request.get<typeof textTypesDropdownResponseSchema, undefined, true>({
    url: '/StaticTexts/list/typefordropdown',
    parser: textTypesDropdownResponseSchema,
    returnData: true,
  });
}

export function createStaticText(payload: CreateStaticTextRequest) {
  return request.post<typeof createStaticTextResponseSchema, CreateStaticTextRequest, true>({
    url: '/StaticTexts',
    parser: createStaticTextResponseSchema,
    payload,
    returnData: true,
  });
}

export function updateStaticText(id: string, payload: UpdateStaticTextRequest) {
  return request.post<typeof updateStaticTextResponseSchema, UpdateStaticTextRequest, true>({
    url: `/StaticTexts/${id}`,
    parser: updateStaticTextResponseSchema,
    payload,
    returnData: true,
  });
}

export function getStaticTextDetail(id: string) {
  return request.get<typeof staticTextDetailResponseSchema, undefined, true>({
    url: `/StaticTexts/${id}`,
    parser: staticTextDetailResponseSchema,
    returnData: true,
  });
}

export function deleteStaticText(id: string) {
  return request.delete<typeof deleteStaticTextResponseSchema, undefined, true>({
    url: `/StaticTexts/${id}`,
    parser: deleteStaticTextResponseSchema,
    returnData: true,
  });
}

export function searchConsumers(params?: ConsumerSearchRequest) {
  return request.get<typeof consumerSearchResponseSchema, ConsumerSearchRequest, true>({
    url: '/Consumers/search',
    parser: consumerSearchResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function getConsumerDetail(id: string) {
  return request.get<typeof consumerDetailResponseSchema, undefined, true>({
    url: `/Consumers/${id}`,
    parser: consumerDetailResponseSchema,
    returnData: true,
  });
}

export function deleteConsumer(id: string) {
  return request.delete<typeof deleteConsumerResponseSchema, undefined, true>({
    url: `/Consumers/${id}`,
    parser: deleteConsumerResponseSchema,
    returnData: true,
  });
}

export function getOrganizationLanguages() {
  return request.get<typeof organizationLanguagesResponseSchema, undefined, true>({
    url: '/OrganizationLanguages/list',
    parser: organizationLanguagesResponseSchema,
    returnData: true,
  });
}

export function searchNotifications(params?: NotificationsSearchRequest) {
  return request.get<typeof notificationsSearchResponseSchema, NotificationsSearchRequest, true>({
    url: '/Notifications/search',
    parser: notificationsSearchResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function createNotification(payload: CreateNotificationRequest) {
  return request.post<typeof createNotificationResponseSchema, CreateNotificationRequest, true>({
    url: '/Notifications',
    parser: createNotificationResponseSchema,
    payload,
    returnData: true,
  });
}

export function updateNotification(id: string, payload: UpdateNotificationRequest) {
  return request.post<typeof updateNotificationResponseSchema, UpdateNotificationRequest, true>({
    url: `/Notifications/${id}`,
    parser: updateNotificationResponseSchema,
    payload,
    returnData: true,
  });
}

export function getNotificationDetail(id: string) {
  return request.get<typeof notificationDetailResponseSchema, undefined, true>({
    url: `/Notifications/${id}`,
    parser: notificationDetailResponseSchema,
    returnData: true,
  });
}

export function deleteNotification(id: string) {
  return request.delete<typeof deleteNotificationResponseSchema, undefined, true>({
    url: `/Notifications/${id}`,
    parser: deleteNotificationResponseSchema,
    returnData: true,
  });
}

export function createBranch(payload: CreateBranchRequest) {
  return request.post<typeof createBranchResponseSchema, CreateBranchRequest, true>({
    url: '/Branches',
    parser: createBranchResponseSchema,
    payload,
    returnData: true,
  });
}

export function updateBranch(id: string, payload: UpdateBranchRequest) {
  return request.post<typeof updateBranchResponseSchema, UpdateBranchRequest, true>({
    url: `/Branches/${id}`,
    parser: updateBranchResponseSchema,
    payload,
    returnData: true,
  });
}

export function deleteBranch(payload: DeleteBranchRequest) {
  return request.delete<typeof deleteBranchResponseSchema, DeleteBranchRequest, true>({
    url: '/Branches',
    parser: deleteBranchResponseSchema,
    payload,
    returnData: true,
  });
}

export function getBranchesList(params?: ListBranchesRequest) {
  return request.get<typeof listBranchesResponseSchema, ListBranchesRequest, true>({
    url: '/Branches/list',
    parser: listBranchesResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function getBranchDetail(id: string) {
  return request.get<typeof branchDetailResponseSchema, undefined, true>({
    url: `/Branches/${id}`,
    parser: branchDetailResponseSchema,
    returnData: true,
  });
}

export function getBranchTemplateFile() {
  return request.get<typeof getBranchTemplateFileResponseSchema, undefined, true>({
    url: '/Branches/gettemplatefile',
    parser: getBranchTemplateFileResponseSchema,
    returnData: true,
  });
}

export function importExcel(payload: ImportExcelRequest) {
  const formData = new FormData();
  formData.append('File', payload.file);

  return request.postFormData<typeof importExcelResponseSchema, true>({
    url: '/Branches/importexcel',
    parser: importExcelResponseSchema,
    formData,
    returnData: true,
  });
}

export function getCitiesListForDropdown() {
  return request.get<typeof citiesDropdownResponseSchema, undefined, true>({
    url: '/Cities/listfordropdown',
    parser: citiesDropdownResponseSchema,
    returnData: true,
  });
}

export function getCountiesListForDropdown() {
  return request.get<typeof countiesDropdownResponseSchema, undefined, true>({
    url: '/Counties/listfordropdown',
    parser: countiesDropdownResponseSchema,
    returnData: true,
  });
}

export function getOrganizationMenuCategoriesForDropdown() {
  return request.get<typeof organizationMenuCategoriesDropdownResponseSchema, undefined, true>({
    url: '/OrganizationMenuCategories/listfordropdown',
    parser: organizationMenuCategoriesDropdownResponseSchema,
    returnData: true,
  });
}

export function searchOrganizationMenuCategories(params?: OrganizationMenuCategoriesSearchRequest) {
  return request.get<typeof organizationMenuCategoriesSearchResponseSchema, OrganizationMenuCategoriesSearchRequest, true>({
    url: '/OrganizationMenuCategories/search',
    parser: organizationMenuCategoriesSearchResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function createOrganizationMenuCategory(payload: CreateOrganizationMenuCategoryRequest) {
  return request.post<typeof createOrganizationMenuCategoryResponseSchema, CreateOrganizationMenuCategoryRequest, true>({
    url: '/OrganizationMenuCategories',
    parser: createOrganizationMenuCategoryResponseSchema,
    payload,
    returnData: true,
  });
}

export function getOrganizationMenuCategoryDetail(id: string) {
  return request.get<typeof organizationMenuCategoryDetailResponseSchema, undefined, true>({
    url: `/OrganizationMenuCategories/${id}`,
    parser: organizationMenuCategoryDetailResponseSchema,
    returnData: true,
  });
}

export function updateOrganizationMenuCategory(id: string, payload: UpdateOrganizationMenuCategoryRequest) {
  return request.post<typeof updateOrganizationMenuCategoryResponseSchema, UpdateOrganizationMenuCategoryRequest, true>({
    url: `/OrganizationMenuCategories/${id}`,
    parser: updateOrganizationMenuCategoryResponseSchema,
    payload,
    returnData: true,
  });
}

export function deleteOrganizationMenuCategory(id: string) {
  return request.delete<typeof deleteOrganizationMenuCategoryResponseSchema, undefined, true>({
    url: `/OrganizationMenuCategories/${id}`,
    parser: deleteOrganizationMenuCategoryResponseSchema,
    returnData: true,
  });
}

export function createOrganizationMenu(payload: CreateOrganizationMenuRequest) {
  return request.post<typeof createOrganizationMenuResponseSchema, CreateOrganizationMenuRequest, true>({
    url: '/OrganizationMenus',
    parser: createOrganizationMenuResponseSchema,
    payload,
    returnData: true,
  });
}

export function getOrganizationMenusList() {
  return request.get<typeof organizationMenusListResponseSchema, undefined, true>({
    url: '/OrganizationMenus/list',
    parser: organizationMenusListResponseSchema,
    returnData: true,
  });
}

export function updateOrganizationMenu(id: string, payload: UpdateOrganizationMenuRequest) {
  return request.post<typeof updateOrganizationMenuResponseSchema, UpdateOrganizationMenuRequest, true>({
    url: `/OrganizationMenus/${id}`,
    parser: updateOrganizationMenuResponseSchema,
    payload,
    returnData: true,
  });
}

export function searchOrganizationMenuProducts(params?: OrganizationMenuProductsSearchRequest) {
  return request.get<typeof organizationMenuProductsSearchResponseSchema, OrganizationMenuProductsSearchRequest, true>({
    url: '/OrganizationMenuProducts/search',
    parser: organizationMenuProductsSearchResponseSchema,
    returnData: true,
    payload: params,
  });
}

export function getOrganizationMenuProductDetail(id: string) {
  return request.get<typeof organizationMenuProductDetailResponseSchema, undefined, true>({
    url: `/OrganizationMenuProducts/${id}`,
    parser: organizationMenuProductDetailResponseSchema,
    returnData: true,
  });
}

export function createOrganizationMenuProduct(payload: CreateOrganizationMenuProductRequest) {
  const formData = new FormData();

  if (payload.name) {
    Object.entries(payload.name).forEach(([lang, name]) => {
      formData.append(`Name[${lang}]`, name);
    });
  }

  if (payload.price !== null && payload.price !== undefined) {
    formData.append('Price', payload.price.toString());
  }
  formData.append('MenuId', payload.menuId.toString());
  formData.append('CategoryId', payload?.categoryId?.toString() || '');
  formData.append('Status', payload.status);

  if (payload.image) {
    payload.image.forEach((img, index) => {
      formData.append(`Image[${index}].LanguageId`, img.languageId);
      formData.append(`Image[${index}].Document`, img.document);
      formData.append(`Image[${index}].EnvironmentType`, img.environmentType);
      if (img.id) {
        formData.append(`Image[${index}].Id`, String(img.id));
      }
    });
  }

  return request.postFormData<typeof createOrganizationMenuProductResponseSchema, true>({
    url: '/OrganizationMenuProducts',
    parser: createOrganizationMenuProductResponseSchema,
    formData,
    returnData: true,
  });
}

export function updateOrganizationMenuProduct(id: string, payload: UpdateOrganizationMenuProductRequest) {
  const formData = new FormData();

  if (payload.name) {
    Object.entries(payload.name).forEach(([lang, name]) => {
      formData.append(`Name[${lang}]`, name);
    });
  }

  if (payload.price !== null && payload.price !== undefined) {
    formData.append('Price', payload.price.toString());
  }
  formData.append('MenuId', payload.menuId.toString());

  formData.append('CategoryId', payload.categoryId.toString());

  formData.append('Status', payload.status);

  if (payload.image) {
    payload.image.forEach((img, index) => {
      formData.append(`Image[${index}].LanguageId`, img.languageId);
      formData.append(`Image[${index}].Document`, img.document);
      formData.append(`Image[${index}].EnvironmentType`, img.environmentType);
      if (img.id) {
        formData.append(`Image[${index}].Id`, String(img.id));
      }
    });
  }

  return request.postFormData<typeof updateOrganizationMenuProductResponseSchema, true>({
    url: `/OrganizationMenuProducts/${id}`,
    parser: updateOrganizationMenuProductResponseSchema,
    formData,
    returnData: true,
  });
}

export function deleteOrganizationMenuProduct(id: string) {
  return request.delete<typeof deleteOrganizationMenuProductResponseSchema, undefined, true>({
    url: `/OrganizationMenuProducts/${id}`,
    parser: deleteOrganizationMenuProductResponseSchema,
    returnData: true,
  });
}
